import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setMemberAdded,
  selectMemberAdded,
} from "../../redux/features/appData/appDataSlice";
import {
  apiGetWorkspaceUsers,
  apiGetUserByEmail,
  apiAddWorkspaceUser,
  apiRemoveWorkspaceUser,
  apiGetUser,
  apiAddInvite,
  apiGetWorkspaceInvites,
  apiDeleteInvite,
  apiResendInvite,
} from "../../api";
import { TRACK_EVENTS, trackAnalyticsEvent } from "../../hooks/useAnalytics";
import { validateEmail } from "../uiHelpers";
import { PlusIcon } from "lucide-react";
import { Button } from "../../ds2/components"

export default function MemberManager() {
  const [addEmail, setAddEmail] = useState(undefined);
  const [validEmail, setValideEmail] = useState(false);
  const [addError, setAddError] = useState("");
  const [workspaceUsers, setWorkspaceUsers] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);

  const storeUser = useSelector((state) => state.appData.user);
  const memberAdded = useSelector(selectMemberAdded);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!storeUser) {
      return;
    }

    (async () => {
      if (!workspaceUsers) {
        await loadData();
      }
    })();
  }, []);

  useEffect(() => {
    if (memberAdded) {
      (async () => {
        await loadData();
      })();
    }
  }, [memberAdded]);

  const loadData = async () => {
    // get users in the workspace
    const [wsUsers, wsInvites] = await Promise.all([
      apiGetWorkspaceUsers(storeUser.currentWorkspaceId),
      apiGetWorkspaceInvites(storeUser.currentWorkspaceId),
    ]);

    console.log("[memberManager/useEffect] got users: ", wsUsers);
    console.log("[memberManager/useEffect] got invites: ", wsInvites);
    setPendingInvites(wsInvites);
    setWorkspaceUsers(wsUsers);
  };

  const addUserToWorkspace = async () => {
    setAddError("");
    const theEmail = addEmail.toLowerCase();

    const foundUser = await apiGetUserByEmail(theEmail);

    if (!foundUser) {
      const foundInviteIndex = pendingInvites.findIndex(
        (invite) => invite.email === theEmail
      );
      if (foundInviteIndex >= 0) {
        setAddError("You've already invited this email!");
        return;
      }

      const result = await apiAddInvite(
        storeUser.id,
        storeUser.currentWorkspaceId,
        theEmail
      );
      trackAnalyticsEvent(TRACK_EVENTS.AddInviteMember, {}, storeUser);

      if (!result) {
        setAddError("Something went wrong with the invite!");
        return;
      }

      setPendingInvites([...pendingInvites, result]);

      toast("Invite to workspace sent.", toastSuccess);
      return;
    }

    const existingUserIndex = workspaceUsers.findIndex(
      (user) => user.id === foundUser.id
    );
    if (existingUserIndex >= 0) {
      setAddError("Already added that user.");
      return;
    }

    if (foundUser.id === storeUser.id) {
      setAddError("Cannot add yourself, silly Goose! 🦆");
      return;
    }

    // good to go
    const workspace = await apiAddWorkspaceUser(
      storeUser.currentWorkspaceId,
      foundUser.id
    );

    const wsUsers = await apiGetWorkspaceUsers(storeUser.currentWorkspaceId);

    setWorkspaceUsers(wsUsers);

    trackAnalyticsEvent(TRACK_EVENTS.AddMember, {}, storeUser);
    dispatch(setMemberAdded(true));
    toast("Added user to workspace.", toastSuccess);
  };

  const removeUser = async (userId) => {
    setAddError("");
    const memberId = userId;
    // make sure the user has more than one workspace
    if (userId === storeUser.id && storeUser.workspaces.length === 1) {
      setAddError("You cannot remove yourself from all workspaces.");
      return;
    }

    // you have to explicitly delete the workspace not remove yourself if you're the last user
    if (userId === storeUser.id && workspaceUsers.length === 1) {
      setAddError("You cannot remove yourself as the last member. You must delete the workspace instead.");
      return;
    }

    await apiRemoveWorkspaceUser(storeUser.currentWorkspaceId, userId);
    trackAnalyticsEvent(TRACK_EVENTS.RemoveMember, {}, storeUser);
    dispatch(setMemberAdded(true));

    // now reload the users
    const wsUsers = await apiGetWorkspaceUsers(storeUser.currentWorkspaceId);
    setWorkspaceUsers([...wsUsers]);

    // if it is the same user, make sure you reload the workspaces list for the sidebar
    if (userId === storeUser.id) {
      const apiUser = apiGetUser(storeUser.id);
      dispatch(setUser(apiUser));
      return;
    }
  };

  const handleEmailChange = (e) => {
    setAddError("");
    const email = e.target.value;

    setAddEmail(email);

    setValideEmail(validateEmail(email));
  };

  const deleteInvite = async (inviteId) => {
    setAddError("");
    // const inviteId = e.target.value;
    const result = await apiDeleteInvite(inviteId);
    if (!result) {
      toast("Unable to delete invite..", toastFail);
      return;
    }
    trackAnalyticsEvent(TRACK_EVENTS.RemoveInvite, {}, storeUser);
    setPendingInvites([
      ...pendingInvites.filter((invite) => invite.id !== inviteId),
    ]);
    toast("Invite removed!", toastSuccess);
  };

  const resendInvite = async (inviteId) => {
    setAddError("");
    // const inviteId = e.target.value;
    const result = await apiResendInvite(inviteId);

    if (!result) {
      toast("Unable to resend invite..", toastFail);
      return;
    }
    trackAnalyticsEvent(TRACK_EVENTS.AddInviteMember, {}, storeUser);
    toast("Invite resent...", toastSuccess);
  };

  console.log("[MemberManager] pendingInvites: ", pendingInvites);

  return (
    <>
      <div className="workspaces-container">
        <div className="workspaces-header">
          <h2>Add a Member</h2>
          <span style={{ color: "var(--text-color-secondary)" }}>
            Add an existing member by email or invite someone new to Winslow.
          </span>
        </div>

        <div className="workspaces-adder">
          <input type="text" value={addEmail} onChange={handleEmailChange} />

          <Button
            key={"add-member-button"}
            // id="add-member-button"
            onClick={addUserToWorkspace}
            disabled={!validEmail}
            leftIcon={<PlusIcon size="16"/>}
          >
            Member
          </Button>
        </div>
        <div className="workspaces-error">{addError} &nbsp;</div>
      </div>

      {workspaceUsers && workspaceUsers.length !== 0 && (
        <div className="workspaces-members">
          <table>
            <thead>
              <tr>
                <th className="workspaces-members-name">Name</th>
                <th className="workspaces-members-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workspaceUsers.map((user, i) => (
                <tr key={`workspaceMember${i}`}>
                  <td className="workspaces-members-name">
                    <img src={user.avatarURL} />
                    <h2>
                      {user.firstName} {user.lastName}
                    </h2>
                    <span
                      style={{
                        color: "var(--text-color-secondary)",
                        fontWeight: "400",
                      }}>
                      {" "}
                      ({user.email}){" "}
                    </span>
                  </td>
                  <td className="workspaces-members-actions">
                    <Button 
                      key={"remove-member-" + user.id}
                      value={user.id} 
                      onClick={() => removeUser(user.id)}
                      variant="secondary"
                      size="sm"
                    >
                      {user.id === storeUser.id ? "Leave" : "Remove"}
                    </Button>
                    {/* } */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingInvites.length > 0 && (
        <div className="workspaces-invites">
          <h2>Invites</h2>
          <table>
            <thead>
              <tr>
                <th className="workspaces-invites-name">Name</th>
                <th className="workspaces-invites-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingInvites &&
                pendingInvites.map((invite, i) => (
                  <tr key={`pendingInviteRow${i}`}>
                    <td className="workspaces-invites-name">
                      <h2>{invite.email}</h2>
                      <span
                        style={{
                          color: "var(--text-color-secondary)",
                          fontWeight: "400",
                        }}>
                        {" "}
                      </span>
                    </td>
                    <td className="workspaces-invites-actions">
                      <Button
                        key={"delete-invite-" + invite.id}
                        variant="secondary"
                        size="sm"
                        value={invite.id}
                        onClick={() => deleteInvite(invite.id)}>
                        Delete
                      </Button>
                      <Button
                        key={"resend-invite-" + invite.id}
                        variant="primary"
                        size="sm"
                        value={invite.id}
                        onClick={() => resendInvite(invite.id)}>
                        Resend
                      </Button>
                      {/* } */}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
